import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import useUsuario from "~fetchs/useUsuario";

const createIconSVG = (color) => {
  const svg = `    <svg width="22" height="38" viewBox="0 0 22 38" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clipPath="url(#clip0_20_2)">
<path d="M3.18236 3.94954C1.59126 5.64764 0.594653 8.12129 0.633351 10.448C0.71416 15.3102 2.90579 17.1508 6.41061 23.8426C7.67311 26.7873 8.99079 29.9032 10.244 35.0637C10.4182 35.8249 10.588 36.532 10.6666 36.5933C10.7451 36.6546 10.915 35.945 11.0892 35.1838C12.3424 30.0232 13.6601 26.9099 14.9226 23.9652C18.4274 17.2733 20.619 15.4327 20.6998 10.5705C20.7385 8.24379 19.7395 5.76766 18.1484 4.06956C16.3308 2.12977 13.5893 0.694173 10.6666 0.634142C7.74386 0.574072 4.99993 2.00974 3.18236 3.94954Z" fill="${color}" stroke="${color}" strokeWidth="1.26462"/>
<path d="M10.6666 14.4675C12.7867 14.4675 14.5054 12.7489 14.5054 10.6288C14.5054 8.50867 12.7867 6.79001 10.6666 6.79001C8.54652 6.79001 6.82782 8.50867 6.82782 10.6288C6.82782 12.7489 8.54652 14.4675 10.6666 14.4675Z" fill="rgba(0, 0, 0, 0.5)"/>
</g>
<defs>
<clipPath id="clip0_20_2">
<rect width="21.3333" height="37.2185" fill="white"/>
</clipPath>
</defs>
</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const icons = {
  current: createIconSVG("#34A853"),
  intermediate: createIconSVG("#F7FD04"),
  destination: createIconSVG("#FF4646"),
};

const RotaDoDiaMapa = ({ rotasCoordenadas }) => {
  const { usuario } = useUsuario();
  const mapRef = useRef(null);
  const [userLocation, setUserLocation] = useState(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const prevCoordsRef = useRef([]);

  useEffect(() => {
    if (!usuario?.slugAcessos?.includes("nao-capturar-lat-long")) {
      const geolocationWatch = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => console.error("Erro ao obter localização:", error)
      );

      return () => navigator.geolocation.clearWatch(geolocationWatch);
    }
  }, [usuario?.slugAcessos]);

  const drawPolyline = (mapInstance, decodedPath) => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }

    polylineRef.current = new globalThis.google.maps.Polyline({
      path: decodedPath,
      geodesic: true,
      strokeColor: "#0000FF",
      strokeOpacity: 1,
      strokeWeight: 4,
      map: mapInstance,
    });

    const bounds = decodedPath.reduce((acc, point) => acc.extend(point), new globalThis.google.maps.LatLngBounds());
    mapInstance.fitBounds(bounds);
  };

  useEffect(() => {
    if (!userLocation || !rotasCoordenadas?.length) return;

    const coordsChanged = JSON.stringify(rotasCoordenadas) !== JSON.stringify(prevCoordsRef.current);
    if (!coordsChanged && mapInstanceRef.current) return;

    prevCoordsRef.current = rotasCoordenadas;

    const initMap = () => {
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new globalThis.google.maps.Map(mapRef.current, {
          center: { lat: userLocation.latitude, lng: userLocation.longitude },
          zoom: 6,
        });
      }
      return mapInstanceRef.current;
    };

    const loadRoute = async (mapInstance) => {
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

        if (!apiKey || apiKey === "YOUR_GOOGLE_MAPS_API_KEY_HERE") {
          console.error("Google Maps API key not configured.");
          return;
        }

        const cacheKey = JSON.stringify({
          userLocation,
          rotasCoordenadas,
        });

        const cached = localStorage.getItem(`route_${cacheKey}`);
        if (cached) {
          const decodedPath = globalThis.google.maps.geometry.encoding.decodePath(JSON.parse(cached));
          drawPolyline(mapInstance, decodedPath);
          return;
        }

        const response = await fetch("https://routes.googleapis.com/directions/v2:computeRoutes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKey,
            "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
          },
          body: JSON.stringify({
            origin: {
              location: {
                latLng: {
                  latitude: userLocation.latitude,
                  longitude: userLocation.longitude,
                },
              },
            },
            destination: {
              location: {
                latLng: {
                  latitude: rotasCoordenadas.at(-1).latitude,
                  longitude: rotasCoordenadas.at(-1).longitude,
                },
              },
            },
            intermediates: rotasCoordenadas.slice(0, -1).map((coord) => ({
              location: {
                latLng: { latitude: coord.latitude, longitude: coord.longitude },
              },
            })),
            travelMode: "DRIVE",
          }),
        });

        const data = await response.json();
        if (data.routes?.[0]?.polyline?.encodedPolyline) {
          const encoded = data.routes[0].polyline.encodedPolyline;

          localStorage.setItem(`route_${cacheKey}`, JSON.stringify(encoded));

          const decodedPath = globalThis.google.maps.geometry.encoding.decodePath(encoded);
          drawPolyline(mapInstance, decodedPath);
        }
      } catch (error) {
        console.error("Erro ao buscar rota:", error);
      }
    };

    const updateMarkers = (mapInstance) => {
      for (const marker of markersRef.current) {
        marker.setMap(null);
      }
      markersRef.current = [];

      markersRef.current.push(
        new globalThis.google.maps.Marker({
          position: { lat: userLocation.latitude, lng: userLocation.longitude },
          map: mapInstance,
          title: "Localização Atual",
          icon: icons.current,
        })
      );

      for (const [index, dest] of rotasCoordenadas.entries()) {
        const isLast = index === rotasCoordenadas.length - 1;
        markersRef.current.push(
          new globalThis.google.maps.Marker({
            position: { lat: dest.latitude, lng: dest.longitude },
            map: mapInstance,
            title: dest.name,
            icon: isLast ? icons.destination : icons.intermediate,
          })
        );
      }
    };

    if (!globalThis.google) {
      console.error("Google Maps API não carregada");
      return;
    }

    const mapInstance = initMap();
    loadRoute(mapInstance);
    updateMarkers(mapInstance);
  }, [userLocation, rotasCoordenadas]);

  return <div ref={mapRef} className="w-full h-[500px] rounded-xl mt-4" />;
};

const MemoizedRotaDoDiaMapa = React.memo(RotaDoDiaMapa);
MemoizedRotaDoDiaMapa.displayName = "RotaDoDiaMapa";

RotaDoDiaMapa.propTypes = {
  rotasCoordenadas: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
};

export default MemoizedRotaDoDiaMapa;
